import { useMemo } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { EnvironmentValue } from '@plerionhq/types-asset-group';
import { apiClient } from './api-client';

export type IntegrationProvider =
  | 'AWS'
  | 'Azure'
  | 'Bitbucket'
  | 'CdrAws'
  | 'GCP'
  | 'GitHub'
  | 'GitLab'
  | 'Kubernetes';

export type KubernetesCapability = 'KSPM' | 'CWPP';

export interface Integration {
  createdAt: string;
  integrationId: string;
  name: string;
  organizationId: string;
  provider: IntegrationProvider;
  tenantId: string;
  type: string;
  updatedAt: string;
  environment?: {
    environmentId: string;
    dimension: { key: 'environment'; value: EnvironmentValue };
  };
  kubernetesCapabilities?: KubernetesCapability[];
  meta: Record<string, unknown>;
  [key: string]: unknown;
}

/** Returns the provider ID and label for the integration if appropriate */
export const getIntegrationProviderId = (
  integration: Integration | undefined,
): { id: string; label: string } | null => {
  if (integration) {
    switch (integration.type) {
      case 'AWSAccount':
      case 'CWPPServiceAccount':
      case 'CdrAws':
        return typeof integration.awsAccountId === 'string'
          ? { id: integration.awsAccountId, label: 'Account ID' }
          : null;
      case 'AzureSubscription':
        return typeof integration.subscriptionId === 'string'
          ? { id: integration.subscriptionId, label: 'Subscription ID' }
          : null;
      case 'AzureActiveDirectory':
        return typeof integration.directoryId === 'string'
          ? { id: integration.directoryId, label: 'Directory ID' }
          : null;
      case 'GCPAccount':
        return typeof integration.projectId === 'string'
          ? { id: integration.projectId, label: 'Project ID' }
          : null;
      case 'Bitbucket':
      case 'GitHub':
        return typeof integration.installationId === 'string'
          ? { id: integration.installationId, label: 'Installation ID' }
          : null;
      case 'GitLab': {
        const gitlabUser = integration.meta?.gitlabUser;
        if (
          gitlabUser &&
          typeof gitlabUser === 'object' &&
          'username' in gitlabUser &&
          typeof gitlabUser.username === 'string'
        ) {
          return { id: gitlabUser.username, label: 'GitLab user' };
        }
        return null;
      }
      default:
        return null;
    }
  }
  return null;
};

export const useGetIntegration = (
  integrationId?: string,
  isOutboundIntegration?: boolean,
) =>
  useQuery({
    queryKey: ['integration', integrationId],
    queryFn: async () => {
      if (isOutboundIntegration) {
        const response = await apiClient.get(
          `/api/core/tenant/outbound-integrations/${integrationId}`,
        );
        return response.data.data;
      } else {
        const response = await apiClient.get(
          `/api/core/tenant/integrations/${integrationId}?include=environment`,
        );
        return response.data.data;
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!integrationId,
  });

export type IntegrationSortProperty = 'createdAt' | 'name';

export type BaseListIntegrationsRequest = {
  enabled?: boolean;
  refetchInterval?: number;
  refetchIntervalInBackground?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  sortBy?: IntegrationSortProperty;
};

export interface ListIntegrationsRequest extends BaseListIntegrationsRequest {
  type?: string;
}

export const useListIntegrations = (
  request: ListIntegrationsRequest = { enabled: true },
) => {
  const queryResult = useListIntegrationsInternalHook(request);
  const { sortBy, type } = request;
  const data = useMemo(() => {
    if (!queryResult.data) {
      return;
    }

    // Filter by type if provided
    let integrations = queryResult.data;
    if (type) {
      integrations = integrations.filter(
        (integration) => integration.type === type,
      );
    }

    return sortIntegrations(integrations, sortBy);
  }, [queryResult.data, sortBy, type]);

  return { ...queryResult, data };
};

const CLOUD_INTEGRATION_TYPES = [
  'AWSAccount',
  'AzureActiveDirectory',
  'AzureSubscription',
  'GCPAccount',
  'Kubernetes',
] as const;

/**
 * Returns only Cloud integrations (including Kubernetes).
 */
export const useListCloudIntegrations = (
  request: BaseListIntegrationsRequest = { enabled: true },
) => {
  const queryResult = useListIntegrationsInternalHook(request);
  const { sortBy } = request;
  const data = useMemo(() => {
    if (!queryResult.data) {
      return;
    }

    const integrations = queryResult.data.filter(({ type }) =>
      CLOUD_INTEGRATION_TYPES.includes(
        type as (typeof CLOUD_INTEGRATION_TYPES)[number],
      ),
    );

    return sortIntegrations(integrations, sortBy);
  }, [queryResult.data, sortBy]);

  return { ...queryResult, data };
};

/**
 * Returns the integrations whose assets Plerion stores: the cloud integrations
 * above, plus code integrations (GitHub, GitLab, Bitbucket, Azure DevOps,
 * CodeCommit), which service-integration marks with category `code`.
 */
export const useListAssetIntegrations = (
  request: BaseListIntegrationsRequest = { enabled: true },
) => {
  const queryResult = useListIntegrationsInternalHook(request);
  const { sortBy } = request;
  const data = useMemo(() => {
    if (!queryResult.data) {
      return;
    }

    const integrations = queryResult.data.filter(
      ({ type, category }) =>
        CLOUD_INTEGRATION_TYPES.includes(
          type as (typeof CLOUD_INTEGRATION_TYPES)[number],
        ) || category === 'code',
    );

    return sortIntegrations(integrations, sortBy);
  }, [queryResult.data, sortBy]);

  return { ...queryResult, data };
};

export type ListIntegrationsInternalHookResponse = {
  data: Integration[];
  // Present only on paginated requests.
  meta?: {
    totalCount: number;
    nextToken?: string;
  };
};

/**
 * Page size for the integrations list fetch. Tenants can hold thousands of
 * integrations; one unbounded request exceeds the backend response limit,
 * so the list is fetched in bounded pages and concatenated.
 */
const INTEGRATIONS_PAGE_LIMIT = 500;

/**
 * Hard cap on page fetches. The backend cursor is strictly increasing so the
 * loop terminates, but a server bug must never turn this into an unbounded
 * request loop.
 */
const MAX_INTEGRATION_PAGES = 100;

export const fetchAllIntegrations = async (): Promise<Integration[]> => {
  const integrations: Integration[] = [];
  let nextToken: string | undefined;
  let pages = 0;

  do {
    const query = new URLSearchParams({
      include: 'environment',
      limit: String(INTEGRATIONS_PAGE_LIMIT),
      ...(nextToken && { nextToken }),
    });
    const response = await apiClient.get<ListIntegrationsInternalHookResponse>(
      `/api/core/tenant/integrations?${query.toString()}`,
    );
    integrations.push(...response.data.data);
    nextToken = response.data.meta?.nextToken;
    pages += 1;
  } while (nextToken && pages < MAX_INTEGRATION_PAGES);

  return integrations;
};

/**
 * Default refetch options for integrations queries.
 * These conservative defaults work well for most use cases.
 * Note: All consumers of useListIntegrations and useListCloudIntegrations
 * share the same query cache and refetch behavior. If you need different
 * refetch behavior, consider using a separate query key.
 * Because refetchOnMount is false, invalidateQueries won't refresh an
 * unmounted list. Mutations must use refetchQueries(['integrations']).
 */
const DEFAULT_REFETCH_OPTIONS = {
  refetchOnMount: false,
  refetchOnWindowFocus: false,
} as const;

// A failure is final for the session: the QueryClient's one retry is the
// only retry, and a remount does not start another (see api/query-state).
const RETRY_ON_MOUNT = false;

/**
 * Internal hook that fetches all integrations from the API.
 * All consumers share the same query cache (same query key: ['integrations']),
 * so refetch settings affect all instances. Use conservative defaults to avoid
 * unnecessary refetching.
 *
 * The returned data is cached by react-query and should not be mutated directly.
 * Public hooks transform the data using `useMemo` to avoid mutating the cache.
 */
const useListIntegrationsInternalHook = (request: ListIntegrationsRequest) => {
  const queryClient = useQueryClient();
  const {
    enabled = true,
    refetchInterval,
    refetchIntervalInBackground,
    refetchOnMount = DEFAULT_REFETCH_OPTIONS.refetchOnMount,
    refetchOnWindowFocus = DEFAULT_REFETCH_OPTIONS.refetchOnWindowFocus,
  } = request;

  return useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const integrations = await fetchAllIntegrations();

      // Populate individual integration caches from list data
      integrations.forEach((integration) => {
        queryClient.setQueryData(
          ['integration', integration.integrationId],
          integration,
        );
      });

      return integrations;
    },
    // Keep the previous result visible during a background refetch so an
    // in-flight/incomplete response can't blank or stale the table.
    placeholderData: keepPreviousData,
    enabled,
    retryOnMount: RETRY_ON_MOUNT,
    refetchInterval,
    refetchIntervalInBackground,
    refetchOnMount,
    refetchOnWindowFocus,
  });
};

/**
 * Sorts integrations by the specified sort order.
 * Uses slice() to avoid mutating the original array.
 */
const sortIntegrations = (
  integrations: Integration[],
  sortBy: IntegrationSortProperty = 'name',
): Integration[] => {
  const sorted = integrations.slice();

  return sorted.sort((a, b) => {
    if (sortBy === 'name') {
      const valueA = (a.name || '').toLowerCase();
      const valueB = (b.name || '').toLowerCase();
      return valueA.localeCompare(valueB);
    }

    // For createdAt, use string comparison (descending order - newest first)
    const valueA = a.createdAt || '';
    const valueB = b.createdAt || '';
    return valueB.localeCompare(valueA);
  });
};
